import {
	type QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { createComment, deleteComment, listComments } from "@/adapters/api";
import type { Comment, Post } from "@/features/feed/types.ts";
import { toComment } from "@/features/feed/utils/map.ts";
import { bumpCommentCount } from "@/features/feed/utils/social.ts";

function bumpCommentCaches(
	queryClient: QueryClient,
	postId: string,
	delta: number,
) {
	queryClient.setQueriesData<Post[]>({ queryKey: ["posts"] }, (posts) =>
		posts?.map((post) =>
			post.id === postId ? bumpCommentCount(post, delta) : post,
		),
	);
	queryClient.setQueriesData<Post>({ queryKey: ["post", postId] }, (post) =>
		post ? bumpCommentCount(post, delta) : post,
	);
}

export function useComments(postId: string) {
	return useQuery({
		queryKey: ["post-comments", postId],
		queryFn: async (): Promise<Comment[]> => {
			const { items } = await listComments(postId);
			return items.map(toComment);
		},
		staleTime: 15_000,
	});
}

export function useCreateComment(postId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (input: { body: string }) => createComment(postId, input),
		onSuccess: (created) => {
			queryClient.setQueryData<Comment[]>(
				["post-comments", postId],
				(comments) => [...(comments ?? []), toComment(created)],
			);
			bumpCommentCaches(queryClient, postId, 1);
		},
	});
}

export function useDeleteComment(postId: string) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (commentId: string) => deleteComment(postId, commentId),
		onSuccess: (_data, commentId) => {
			queryClient.setQueryData<Comment[]>(
				["post-comments", postId],
				(comments) => comments?.filter((comment) => comment.id !== commentId),
			);
			bumpCommentCaches(queryClient, postId, -1);
		},
	});
}
